/**
 * Provider-Neutral Listing Publication Contract
 *
 * The canonical, typed shape of the "validate / dry run" result returned by the
 * marketplace-sync / inventory-sync edge functions (eBay, BigCommerce, and any
 * future provider). Provider adapters translate their own API responses into
 * this single contract, so components never reach into raw provider JSONB with
 * `any`.
 *
 * This file is deliberately dependency-free and side-effect-free: the parser
 * below is a defensive coercion boundary that accepts untrusted JSON (the
 * `unknown` payload from `supabase.functions.invoke`) and returns a safe,
 * fully-typed object. Malformed or missing fields degrade to empty/defaults
 * rather than throwing or leaking raw provider shapes into the UI.
 */

export type ListingRunMode = "NOT_CONFIGURED" | "DRY_RUN" | "LIVE";

/** A single validation or blocking problem reported by the publication plan. */
export interface ListingIssue {
  code: string;
  message: string;
}

/** A single step in the deterministic publication plan. */
export interface ListingPlanStep {
  step: number;
  name: string;
  skipped?: boolean;
  count?: number;
}

/** The publication plan produced by the edge function during a dry run. */
export interface ListingPlan {
  steps: ListingPlanStep[];
  validation_issues: ListingIssue[];
}

/**
 * Canonical result of a listing "validate / dry run" invocation.
 * Provider-neutral: eBay, BigCommerce and future providers all emit this shape.
 */
export interface ListingDryRunResult {
  success: boolean;
  mode: ListingRunMode;
  message: string;
  error?: string;
  plan?: ListingPlan;
  /** Intended canonical records (no external IDs created during a dry run). */
  intended_items?: unknown[];
  intended_offers?: unknown[];
  intended_product?: { name: string };
  blockers?: ListingIssue[];
}

// ---------------------------------------------------------------------------
// Defensive coercion helpers — accept unknown, return safe primitives.
// ---------------------------------------------------------------------------

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function asString(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function asNumber(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

function asIssue(value: unknown): ListingIssue {
  const record = asRecord(value);
  return {
    code: asString(record?.code),
    message: asString(record?.message),
  };
}

function asIssues(value: unknown): ListingIssue[] {
  return Array.isArray(value) ? value.map(asIssue) : [];
}

function asPlanStep(value: unknown): ListingPlanStep {
  const record = asRecord(value);
  return {
    step: asNumber(record?.step),
    name: asString(record?.name),
    skipped: typeof record?.skipped === "boolean" ? record.skipped : undefined,
    count: typeof record?.count === "number" ? record.count : undefined,
  };
}

function asPlanSteps(value: unknown): ListingPlanStep[] {
  return Array.isArray(value) ? value.map(asPlanStep) : [];
}

/**
 * Parse an untrusted dry-run payload into the canonical typed contract.
 *
 * Never throws: a null, non-object, or malformed payload degrades to a safe
 * default result so the UI can render an honest "no plan produced" state rather
 * than crashing on a malformed edge-function response.
 */
export function parseListingDryRunResult(raw: unknown): ListingDryRunResult {
  const root = asRecord(raw) ?? {};
  const plan = asRecord(root.plan);
  const intendedProduct = asRecord(root.intended_product);

  const modeRaw = asString(root.mode).toUpperCase();
  const mode: ListingRunMode =
    modeRaw === "LIVE" || modeRaw === "DRY_RUN" ? modeRaw : "NOT_CONFIGURED";

  // Normalise provider divergence: eBay nests the publication steps under
  // `plan.steps`, while BigCommerce returns them at the top level as `steps`.
  // Both collapse into the single canonical `plan.steps` shape here.
  const steps = plan ? asPlanSteps(plan.steps) : asPlanSteps(root.steps);
  const validationIssues = plan ? asIssues(plan.validation_issues) : [];
  const normalizedPlan: ListingPlan | undefined =
    plan || steps.length > 0 ? { steps, validation_issues: validationIssues } : undefined;

  return {
    success: typeof root.success === "boolean" ? root.success : false,
    mode,
    message: asString(root.message),
    error: root.error != null ? asString(root.error) : undefined,
    plan: normalizedPlan,
    intended_items: Array.isArray(root.intended_items) ? root.intended_items : undefined,
    intended_offers: Array.isArray(root.intended_offers) ? root.intended_offers : undefined,
    intended_product:
      intendedProduct && typeof intendedProduct.name === "string"
        ? { name: intendedProduct.name }
        : undefined,
    blockers: asIssues(root.blockers),
  };
}